import React, { useState } from 'react';
import { ChevronDown, Search, CheckCircle } from 'lucide-react';
import { COUNTRIES } from './constants';

/**
 * Searchable country dropdown — supports single or multi-select
 * @param {string|string[]} value - Selected country code(s)
 * @param {function} onChange - Callback with selected country code(s)
 * @param {boolean} multiple - Enable multi-select mode
 * @param {string} placeholder - Placeholder text
 */
export default function CountrySelect({ 
  value, 
  onChange, 
  multiple = false, 
  placeholder = 'Search country…' 
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const filtered = COUNTRIES.filter((c) =>
    c.name.toLowerCase().includes(query.toLowerCase()) ||
    c.code.toLowerCase().includes(query.toLowerCase())
  );

  const selected = multiple ? (value || []) : (value ? [value] : []);

  const toggle = (code) => {
    if (multiple) {
      onChange(selected.includes(code) 
        ? selected.filter((c) => c !== code) 
        : [...selected, code]
      );
    } else {
      onChange(code);
      setOpen(false);
    }
  };

  const label = multiple
    ? (selected.length 
        ? selected.map((c) => COUNTRIES.find((x) => x.code === c)?.name).join(', ') 
        : placeholder)
    : (selected[0] 
        ? COUNTRIES.find((c) => c.code === selected[0])?.name 
        : placeholder);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full text-left px-4 py-2 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg text-xs flex items-center justify-between gap-2 truncate hover:border-[var(--accent-primary)] transition-smooth"
      >
        <span className="truncate text-[var(--text-primary)]">{label}</span>
        <ChevronDown 
          className={`w-3.5 h-3.5 shrink-0 text-[var(--text-tertiary)] transition-transform ${
            open ? 'rotate-180' : ''
          }`} 
        />
      </button>

      {open && (
        <div className="absolute z-30 mt-1 w-full bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-lg shadow-lg max-h-48 flex flex-col">
          <div className="flex items-center gap-1.5 px-2 py-1.5 border-b border-[var(--border-color)]">
            <Search className="w-3.5 h-3.5 text-[var(--text-tertiary)] shrink-0" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search…"
              className="flex-1 text-xs outline-none bg-transparent text-[var(--text-primary)] placeholder-[var(--text-tertiary)]"
            />
          </div>

          <div className="overflow-y-auto flex-1">
            {filtered.map((c) => {
              const isOn = selected.includes(c.code);
              return (
                <button
                  key={c.code}
                  type="button"
                  onClick={() => toggle(c.code)}
                  className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 hover:bg-[var(--bg-secondary)] transition-smooth ${
                    isOn ? 'bg-[var(--accent-primary)]/10' : ''
                  }`}
                >
                  {multiple && (
                    <span
                      className={`w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 ${
                        isOn 
                          ? 'bg-[var(--accent-primary)] border-[var(--accent-primary)]' 
                          : 'border-[var(--border-color)]'
                      }`}
                    >
                      {isOn && <CheckCircle className="w-2.5 h-2.5 text-white" />}
                    </span>
                  )}
                  <span className="text-[var(--text-secondary)] font-mono w-6 shrink-0">
                    {c.code}
                  </span>
                  <span
                    className={isOn 
                      ? 'text-[var(--accent-primary)] font-medium' 
                      : 'text-[var(--text-primary)]'
                    }
                  >
                    {c.name}
                  </span>
                </button>
              );
            })}
            {filtered.length === 0 && (
              <p className="text-xs text-[var(--text-tertiary)] px-3 py-2">No match</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}